import type { Notification } from '../types';
import apiClient from '@/api';

/**
 * Notifications repository — the real feed.
 *
 * Reads the org's genuine recent activity from `GET /v1/notifications`
 * (completed/failed backtests, members joining, strategies, clients, accounts,
 * live models) and maps it to the bell's shape. That feed is *derived* (there
 * is no notifications table), so read-state has no server home — we track read
 * ids client-side in localStorage so the unread badge behaves. On staging/prod
 * this attaches to the same real endpoint; no mock data anywhere.
 */

interface ApiNotification {
  id: string;
  type: string;
  title: string;
  subtitle: string;
  /**
   * `ready | attention | info` in practice, but the API declares it as a bare
   * `str` with no enum (`notifications/router.py`), so a new server-side tone
   * would arrive here as a string this UI has never heard of. Typed as `string`
   * so the fallback below is a real branch rather than dead code.
   */
  tone: string;
  at: string;
}

const READ_KEY = 'biasafe.notifications.read';

function readSet(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(READ_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

function persist(set: Set<string>): void {
  try {
    localStorage.setItem(READ_KEY, JSON.stringify([...set]));
  } catch {
    /* ignore quota / private-mode failures */
  }
}

const TONE_TO_TYPE: Record<string, Notification['type'] | undefined> = {
  ready: 'success',
  attention: 'error',
  info: 'info',
};

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

export const notificationRepository = {
  getNotifications: async (): Promise<Array<Notification>> => {
    // `items` is required in the OpenAPI schema, but declared optional here so
    // the `?? []` below is a real guard against a malformed 200 rather than
    // code the type checker considers unreachable.
    const { data } = await apiClient.get<{ items?: Array<ApiNotification> }>(
      '/notifications',
    );
    const read = readSet();
    return (data.items ?? []).map((n) => ({
      id: n.id,
      title: n.title,
      description: n.subtitle,
      timestamp: relativeTime(n.at),
      // Kept alongside the relative string, not instead of it. `relativeTime`
      // is computed once at fetch, so an open panel's "2m ago" never advances
      // and the instant it was derived from is unrecoverable — the dialog
      // needs the real one to group by day and to show an absolute date.
      at: n.at,
      // The server's twelve-value event type. It arrived on the wire and was
      // dropped here for a long time, which is why the UI could only ever
      // speak in three tones about twelve different things.
      eventType: n.type,
      isRead: read.has(n.id),
      type: TONE_TO_TYPE[n.tone] ?? 'info',
    }));
  },

  markAsRead: (id: string): Promise<void> => {
    const read = readSet();
    read.add(id);
    persist(read);
    return Promise.resolve();
  },

  /**
   * Mark a whole batch at once.
   *
   * One read/modify/write, not one per id. Calling `markAsRead` in a loop would
   * re-read the set and re-serialise the whole array twelve times, and — worse
   * — each call starts from `localStorage`, so two overlapping loops can each
   * persist a set that never saw the other's writes.
   */
  markAllAsRead: (ids: Array<string>): Promise<void> => {
    const read = readSet();
    for (const id of ids) read.add(id);
    persist(read);
    return Promise.resolve();
  },
};
